import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import FleetVehicleSelect from "../VehicleManagement/FleetVehicleSelect";

const BASE_URL = API_BASE_URL;

const DriverTimeSheet = () => {
  const [tracking, setTracking] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [records, setRecords] = useState([]);
  const [driverName, setDriverName] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");

  useEffect(() => {
    let interval;
    if (tracking) {
      interval = setInterval(() => setElapsed((e) => e + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [tracking]);

  const fetchRecords = () =>
    axios.get(`${BASE_URL}/onboarding/timesheet`)
      .then((r) => setRecords(r.data || []))
      .catch(() => {});

  useEffect(() => { fetchRecords(); }, []);

  const fmt = (secs) => {
    const h = Math.floor(secs / 3600).toString().padStart(2, "0");
    const m = Math.floor((secs % 3600) / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const handleStart = () => {
    setStartTime(new Date());
    setElapsed(0);
    setTracking(true);
  };

  const handleStop = async () => {
    setTracking(false);
    const endTime = new Date();
    const totalMins = Math.round(elapsed / 60);
    try {
      await axios.post(`${BASE_URL}/onboarding/timesheet`, {
        driverName, vehicleNumber,
        date: new Date().toISOString().split("T")[0],
        startTime: startTime?.toLocaleTimeString(),
        endTime: endTime.toLocaleTimeString(),
        totalHours: (elapsed / 3600).toFixed(2),
        totalMinutes: totalMins,
      });
      fetchRecords();
    } catch {
      alert("Save failed — check backend");
    }
    setElapsed(0);
  };

  const s = {
    container: {
      fontFamily: "Arial, sans-serif",
      marginLeft: "278px",
      marginRight: "10px",
      backgroundColor: "#ffffff",
      color: "#000000",
      minHeight: "100vh",
    },
    pageHeader: {
      backgroundColor: "#FFC107",
      padding: "16px 24px",
      marginBottom: "0px",
    },
    pageHeaderText: {
      fontSize: "22px",
      fontWeight: "700",
      color: "#000000",
      letterSpacing: "1px",
      textTransform: "uppercase",
      margin: 0,
    },
    contentBody: {
      padding: "24px",
    },
    sectionTitle: {
      fontSize: "15px",
      fontWeight: "700",
      color: "#000000",
      marginBottom: "4px",
    },
    sectionDivider: {
      border: "none",
      borderTop: "2px solid #FFC107",
      marginBottom: "16px",
    },
    inputRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "16px",
      marginBottom: "16px",
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    label: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#000000",
    },
    input: {
      padding: "8px 10px",
      border: "1.5px solid #000000",
      borderRadius: "0",
      fontSize: "13px",
      backgroundColor: "#ffffff",
      color: "#000000",
      outline: "none",
      boxSizing: "border-box",
    },
    inputDisabled: {
      padding: "8px 10px",
      border: "1.5px solid #000000",
      borderRadius: "0",
      fontSize: "13px",
      backgroundColor: "#f5f5f5",
      color: "#000000",
      outline: "none",
      boxSizing: "border-box",
      cursor: "not-allowed",
    },
    timerBox: {
      textAlign: "center",
      padding: "32px 24px",
      border: "1.5px solid #000000",
      backgroundColor: tracking ? "#fffde7" : "#f5f5f5",
      marginBottom: "16px",
    },
    timer: {
      fontSize: "52px",
      fontWeight: "700",
      fontVariantNumeric: "tabular-nums",
      color: tracking ? "#000000" : "#555555",
      letterSpacing: "6px",
    },
    timerNote: {
      fontSize: "12px",
      color: "#555555",
      marginTop: "8px",
      fontWeight: "700",
    },
    btnRow: {
      display: "flex",
      gap: "12px",
      justifyContent: "center",
      marginTop: "16px",
    },
    startBtn: {
      backgroundColor: tracking ? "#cccccc" : "#FFC107",
      color: "#000000",
      border: "none",
      padding: "8px 28px",
      borderRadius: "0",
      cursor: tracking ? "not-allowed" : "pointer",
      fontWeight: "700",
      fontSize: "13px",
      textTransform: "uppercase",
    },
    stopBtn: {
      backgroundColor: !tracking ? "#cccccc" : "#000000",
      color: !tracking ? "#000000" : "#ffffff",
      border: "none",
      padding: "8px 28px",
      borderRadius: "0",
      cursor: !tracking ? "not-allowed" : "pointer",
      fontWeight: "700",
      fontSize: "13px",
      textTransform: "uppercase",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: "8px",
    },
    th: {
      backgroundColor: "#000000",
      color: "#ffffff",
      padding: "12px 16px",
      textAlign: "center",
      fontSize: "13px",
      fontWeight: "700",
      border: "1px solid #000000",
    },
    td: {
      border: "1px solid #000000",
      padding: "10px 14px",
      fontSize: "13px",
      textAlign: "center",
      backgroundColor: "#ffffff",
      color: "#000000",
    },
    tdEmpty: {
      border: "1px solid #000000",
      padding: "24px 14px",
      fontSize: "13px",
      textAlign: "center",
      backgroundColor: "#ffffff",
      color: "#888888",
    },
  };

  return (
    <div style={s.container}>
      {/* Yellow header bar */}
      <div style={s.pageHeader}>
        <h1 style={s.pageHeaderText}>Driver Timesheet</h1>
      </div>

      <div style={s.contentBody}>

        {/* Driver Info */}
        <div style={s.sectionTitle}>Driver Information</div>
        <hr style={s.sectionDivider} />
        <div style={s.inputRow}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Driver Name</label>
            <input
              style={tracking ? s.inputDisabled : s.input}
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              placeholder="Enter driver name"
              disabled={tracking}
            />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Vehicle Number</label>
            <FleetVehicleSelect
              name="vehicleNumber"
              value={vehicleNumber}
              onChange={(e) => setVehicleNumber(e.target.value)}
              style={tracking ? s.inputDisabled : s.input}
              disabled={tracking}
              placeholderOption="Select vehicle"
            />
          </div>
        </div>

        {/* Timer */}
        <div style={s.sectionTitle}>Duty Timer</div>
        <hr style={s.sectionDivider} />
        <div style={s.timerBox}>
          <div style={s.timer}>{fmt(elapsed)}</div>
          <div style={s.timerNote}>
            {tracking
              ? `Started at ${startTime?.toLocaleTimeString()}`
              : "Press Start to begin tracking"}
          </div>
          <div style={s.btnRow}>
            <button style={s.startBtn} disabled={tracking} onClick={handleStart}>
              ▶ Start Duty
            </button>
            <button style={s.stopBtn} disabled={!tracking} onClick={handleStop}>
              ■ End Duty
            </button>
          </div>
        </div>

        {/* Records Table */}
        <div style={{ ...s.sectionTitle, marginTop: "24px" }}>Timesheet Records</div>
        <hr style={s.sectionDivider} />
        <table style={s.table}>
          <thead>
            <tr>
              {["Driver", "Vehicle", "Date", "Start", "End", "Hours", "Minutes"].map((h) => (
                <th style={s.th} key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.length ? (
              records.map((r, i) => (
                <tr key={i}>
                  <td style={s.td}>{r.driverName}</td>
                  <td style={s.td}>{r.vehicleNumber}</td>
                  <td style={s.td}>{r.date}</td>
                  <td style={s.td}>{r.startTime}</td>
                  <td style={s.td}>{r.endTime}</td>
                  <td style={s.td}><strong>{r.totalHours}h</strong></td>
                  <td style={s.td}>{r.totalMinutes} min</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={s.tdEmpty}>No timesheet records yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DriverTimeSheet;